import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { Conversation } from 'src/app/demo/model/conversation.model';
import { Guardian, Student } from 'src/app/demo/model/entity-relation.model';
import { EntityApiService } from 'src/app/demo/service/entity-api.service';
import { FirestoreChatService } from 'src/app/demo/service/firestore-chat.service';
import { WebStorage } from '../../auth/storage/web.storage';
import { OperationService } from 'src/app/demo/service/operation.service';
import { MessagePushPayload } from 'src/app/demo/model/MessageDTO';


@Injectable()
export class NewMessageFacade {
  private get currentUser() { const u = this.webStorage.getUserData(); return { ...u, schoolId: String(u.schoolId), participantKey: `${u.role}_${u.refTableId}` }; }

  students$: Observable<Student[]>;
  guardians$: Observable<Guardian[]>;

  constructor(
    private firestoreService: FirestoreChatService,
    private entityApiService: EntityApiService,
    private webStorage : WebStorage,
    private operationService: OperationService
  ) {
    this.students$ = this.entityApiService.getStudents();
    this.guardians$ = this.entityApiService.getGuardians();
  }

  private async runInChunks<T, R>(items: T[], size: number, fn: (chunk: T[]) => Promise<R[]>): Promise<R[]> {
    const results: R[] = [];
    for (let i = 0; i < items.length; i += size) {
      const chunk = items.slice(i, i + size);
      results.push(...await fn(chunk));
    }
    return results;
  }

  private deriveStudentStatus(studentId: string): string {
    const student = this.entityApiService.getEntityRelation().value?.students?.find(
      (s: any) => s.id.toString() === studentId
    );
    if (!student) return 'active';
    return student.profileStatus === 'I' ? 'inactive' : student.profileStatus === 'Upcoming' ? 'upcoming' : 'active';
  }

  createGroupConversation(student: Student): void {
    const conversationId = `std-${student.id}-grp`;
    const participantIds = this.entityApiService.getParticipantKeysForType('studentGroup', student.id.toString(), this.currentUser.schoolId);
    const participants: Record<string, boolean> = {};
    participantIds.forEach(id => participants[id] = true);

    const conversation: Conversation = {
      id: conversationId,
      type: 'GROUP',
      studentId: student.id.toString(),
      classroomId: student.roomId != null ? String(student.roomId) : null,
      schoolId: this.currentUser.schoolId.toString(),
      participants,
      lastMessage: '',
      lastMessageAt: new Date(),
      isActive: true,
      unreadCount: {},
      studentStatus: this.deriveStudentStatus(student.id.toString())
    };

    this.firestoreService.createConversation(conversation);
  }

  createPrivateConversation(guardian: Guardian): void {
    const studentId = guardian.studentIds[0].toString();
    const conversationId = `std-${studentId}-pvt-${guardian.id}`;
    const participantIds = this.entityApiService.getParticipantKeysForType('studentPrivate', studentId, this.currentUser.schoolId, guardian.id.toString());
    const participants: Record<string, boolean> = {};
    participantIds.forEach(id => participants[id] = true);

    const studentForRoom = this.entityApiService.getEntityRelation().value?.students
      ?.find((s: any) => s.id.toString() === studentId);
    const conversation: Conversation = {
      id: conversationId,
      type: 'PRIVATE_PARENT',
      studentId,
      classroomId: studentForRoom?.roomId != null ? String(studentForRoom.roomId) : null,
      schoolId: this.currentUser.schoolId.toString(),
      participants,
      lastMessage: '',
      lastMessageAt: new Date(),
      isActive: true,
      unreadCount: {},
      studentStatus: this.deriveStudentStatus(studentId)
    };

    this.firestoreService.createConversation(conversation);
  }

  createBroadcastConversations(students: Student[]): void {
    students.forEach(student => this.createGroupConversation(student));
  }

  async broadcastToStudents(selectedStudents: any[], text: string): Promise<{ fulfilled: number; skipped: string[]; failed: number; failedNames: string[] }> {
    const validStudents = selectedStudents.filter(student => student.guardianIds && student.guardianIds.length > 0);
    const skippedStudents = selectedStudents.filter(student => !student.guardianIds || student.guardianIds.length === 0);

    if (validStudents.length === 0) {
      return { fulfilled: 0, skipped: skippedStudents.map((s: any) => s.name), failed: 0, failedNames: [] };
    }

    // Check conversation existence in chunks of 50 to avoid Firestore rate limits
    const existenceChecks = await this.runInChunks(validStudents, 50, async (chunk) => {
      return Promise.all(
        chunk.map(async student => {
          const conversationId = `std-${student.id}-grp`;
          const { exists, participantIds } = await this.resolveParticipantIdsForStudentGroup(
            student.id.toString(),
            this.currentUser.schoolId
          );
          return { conversationId, exists, studentId: student.id.toString(), participantIds };
        })
      );
    });

    // Build one batch per recipient
    const batchEntities = this.entityApiService.getEntityRelation().value;
    const batches = existenceChecks.map(({ conversationId, exists, studentId, participantIds }) => {
      const studentForRoom = batchEntities?.students?.find((s: any) => s.id.toString() === studentId);
      const params = {
        conversationId,
        sender: this.currentUser.participantKey,
        text,
        conversationExists: exists,
        participantIds,
        schoolId: this.currentUser.schoolId,
        type: 'studentGroup' as const,
        studentId,
        studentStatus: this.deriveStudentStatus(studentId),
        classroomId: studentForRoom?.roomId != null ? String(studentForRoom.roomId) : null,
      };
      return this.firestoreService.buildSendBatch(params);
    });

    // Commit batches in chunks of 50 to avoid overwhelming Firestore
    const results = await this.runInChunks(batches, 50, async (chunk) => {
      return Promise.allSettled(chunk.map(b => b.commit())) as Promise<PromiseSettledResult<void>[]>;
    });

    const fulfilled = results.filter(r => r.status === 'fulfilled').length;
    const rejectedIndices = results.map((r, i) => ({ r, i })).filter(({ r }) => r.status === 'rejected');
    rejectedIndices.forEach(({ r }) => console.error('Batch commit failed:', (r as PromiseRejectedResult).reason));
    const failedNames = rejectedIndices.map(({ i }) => validStudents[i]?.name || '').filter(Boolean);

    // Send push notifications for successful sends (chunked)
    const successfulStudentItems = existenceChecks.filter((_, i) => results[i].status === 'fulfilled');
    await this.runInChunks(successfulStudentItems, 50, async (chunk) => {
      await Promise.allSettled(
        chunk.map(({ conversationId, participantIds, studentId }) => {
          const student = validStudents.find((s: any) => s.id.toString() === studentId);
          return this.sendPushNotificationForConversation(text, conversationId, participantIds, 'studentGroup', student?.name || '')
            .catch(err => console.error('Push failed for', conversationId, err));
        })
      );
      return [];
    });

    return { fulfilled, skipped: skippedStudents.map((s: any) => s.name), failed: failedNames.length, failedNames };
  }

  createStudentPrivateConversation(student: Student, guardianId: string): void {
    const conversationId = `std-${student.id}-pvt-${guardianId}`;
    const participantIds = this.entityApiService.getParticipantKeysForType('studentPrivate', student.id.toString(), this.currentUser.schoolId, guardianId);
    const participants: Record<string, boolean> = {};
    participantIds.forEach(id => participants[id] = true);

    const conversation: Conversation = {
      id: conversationId,
      type: 'PRIVATE_PARENT',
      studentId: student.id.toString(),
      classroomId: student.roomId != null ? String(student.roomId) : null,
      schoolId: this.currentUser.schoolId.toString(),
      participants,
      lastMessage: '',
      lastMessageAt: new Date(),
      isActive: true,
      unreadCount: {},
      studentStatus: this.deriveStudentStatus(student.id.toString())
    };

    this.firestoreService.createConversation(conversation);
  }

  createAdminGroupConversation(student: Student): void {
    const conversationId = `std-${student.id}-adm-grp`;
    const participantIds = this.entityApiService.getParticipantKeysForType('adminGroup', student.id.toString(), this.currentUser.schoolId);
    const participants: Record<string, boolean> = {};
    participantIds.forEach(id => participants[id] = true);

    const conversation: Conversation = {
      id: conversationId,
      type: 'ADMIN_CHAT',
      studentId: student.id.toString(),
      classroomId: student.roomId != null ? String(student.roomId) : null,
      schoolId: this.currentUser.schoolId.toString(),
      participants,
      lastMessage: '',
      lastMessageAt: new Date(),
      isActive: true,
      unreadCount: {},
      studentStatus: this.deriveStudentStatus(student.id.toString())
    };

    this.firestoreService.createConversation(conversation);
  }

  createAdminPrivateConversation(student: Student, guardianId: string): void {
    const conversationId = `std-${student.id}-adm-pvt-${guardianId}`;
    const participantIds = this.entityApiService.getParticipantKeysForType('adminPrivate', student.id.toString(), this.currentUser.schoolId, guardianId);
    const participants: Record<string, boolean> = {};
    participantIds.forEach(id => participants[id] = true);

    const conversation: Conversation = {
      id: conversationId,
      type: 'ADMIN_PRIVATE',
      studentId: student.id.toString(),
      classroomId: student.roomId != null ? String(student.roomId) : null,
      schoolId: this.currentUser.schoolId.toString(),
      participants,
      lastMessage: '',
      lastMessageAt: new Date(),
      isActive: true,
      unreadCount: {},
      studentStatus: this.deriveStudentStatus(student.id.toString())
    };

    this.firestoreService.createConversation(conversation);
  }

  async broadcastToStaff(selectedStaff: any[], text: string): Promise<{ fulfilled: number; failed: number; failedNames: string[] }> {
    const senderId = this.currentUser.refTableId.toString();

    // Check conversation existence in chunks of 50
    const existenceChecks = await this.runInChunks(selectedStaff, 50, async (chunk) => {
      return Promise.all(
        chunk.map(staff => {
          const recipientId = staff.id.toString();
          const [small, large] = [senderId, recipientId].sort();
          const conversationId = `dm-${small}-${large}`;
          const participantIds = [`staff_${senderId}`, `staff_${recipientId}`];
          return this.firestoreService.conversationExists(conversationId).then(exists => ({
            conversationId,
            exists,
            participantIds
          }));
        })
      );
    });

    const batches = existenceChecks.map(({ conversationId, exists, participantIds }) => {
      const params = {
        conversationId,
        sender: this.currentUser.participantKey,
        text,
        conversationExists: exists,
        participantIds,
        schoolId: this.currentUser.schoolId,
        type: 'directMessage' as const,
      };
      return this.firestoreService.buildSendBatch(params);
    });

    // Commit batches in chunks of 50
    const results = await this.runInChunks(batches, 50, async (chunk) => {
      return Promise.allSettled(chunk.map(b => b.commit())) as Promise<PromiseSettledResult<void>[]>;
    });

    const fulfilled = results.filter(r => r.status === 'fulfilled').length;
    const rejectedIndices = results.map((r, i) => ({ r, i })).filter(({ r }) => r.status === 'rejected');
    rejectedIndices.forEach(({ r }) => console.error('Batch commit failed:', (r as PromiseRejectedResult).reason));
    const failedNames = rejectedIndices.map(({ i }) => selectedStaff[i]?.name || '').filter(Boolean);

    // Send push notifications for successful sends (chunked)
    const successfulStaffItems = existenceChecks.filter((_, i) => results[i].status === 'fulfilled');
    await this.runInChunks(successfulStaffItems, 50, async (chunk) => {
      await Promise.allSettled(
        chunk.map(({ conversationId, participantIds }) =>
          this.sendPushNotificationForConversation(text, conversationId, participantIds, 'directMessage', '')
            .catch(err => console.error('Push failed for', conversationId, err))
        )
      );
      return [];
    });

    return { fulfilled, failed: failedNames.length, failedNames };
  }

  private async sendPushNotificationForConversation(
    messageText: string,
    conversationId: string,
    participantIds: string[],
    type: string,
    studentName: string
  ): Promise<void> {
    const senderKey = this.currentUser.participantKey;
    const senderRoleRaw = senderKey.split('_')[0];
    const { staffIds, guardianIds } = this.parseParticipantKeys(participantIds, senderKey, studentName);
    if (staffIds.length === 0 && guardianIds.length === 0) return;
    const isDM = type === 'directMessage';
    const payload: MessagePushPayload = {
      staffIds,
      guardianIds: isDM ? [] : guardianIds,
      message: messageText,
      senderName: this.currentUser.firstName || 'User',
      senderRole: senderRoleRaw === 'staff' ? 'Staff' : 'Guardian',
      receiverRole: isDM ? 'Staff' : (senderRoleRaw === 'staff' ? 'Guardian' : 'Staff'),
      schoolId: this.currentUser.schoolId,
      chatId: [conversationId],
      from: 'Parent Messaging',
    };
    await firstValueFrom(this.operationService.pushMessage(payload));
  }

  private ensureSenderInParticipants(ids: string[]): string[] {
    const key = this.currentUser.participantKey;
    return ids.includes(key) ? ids : [...ids, key];
  }

  private async resolveParticipantIdsForStudentGroup(
    studentId: string,
    schoolId: string
  ): Promise<{ exists: boolean; participantIds: string[] }> {
    const convId = `std-${studentId}-grp`;
    const conv = await this.firestoreService.getConversationOnce(convId);
    if (conv) {
      return { exists: true, participantIds: this.ensureSenderInParticipants(conv.participantIds ?? []) };
    }
    return {
      exists: false,
      participantIds: this.ensureSenderInParticipants(
        this.entityApiService.getParticipantKeysForType('studentGroup', studentId, schoolId)
      ),
    };
  }

  private parseParticipantKeys(
    participantIds: string[],
    senderKey: string,
    studentName: string
  ): { staffIds: number[], guardianIds: { guardianId: number, studentName: string }[] } {
    const staffIds: number[] = [];
    const guardianIds: { guardianId: number, studentName: string }[] = [];
    participantIds
      .filter(key => key !== senderKey)
      .forEach(key => {
        if (key.startsWith('staff_')) {
          staffIds.push(Number(key.replace('staff_', '')));
        } else if (key.startsWith('guardian_') || key.startsWith('parent_')) {
          guardianIds.push({
            guardianId: Number(key.replace('guardian_', '').replace('parent_', '')),
            studentName
          });
        }
      });
    return { staffIds, guardianIds };
  }
}
